/**
 * Storefront read of the live urgency campaign + per-product labels.
 *
 * Reads go through the browser Supabase client: RLS only exposes campaigns
 * that are enabled and inside their time window, and labels that are enabled.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  campaignStatus,
  type ProductUrgencyLabel,
  type UrgencyCampaign,
} from "@/lib/urgency";

async function fetchLiveCampaign(): Promise<UrgencyCampaign | null> {
  const nowIso = new Date().toISOString();
  // Filter explicitly: admins can read every row via RLS, so without this an
  // unlaunched draft would shadow the campaign that is actually live.
  const { data, error } = await supabase
    .from("urgency_campaigns")
    .select("id,name,kind,headline,subline,coupon_code,accent,starts_at,ends_at,placements,is_enabled")
    .eq("is_enabled", true)
    .or(`starts_at.is.null,starts_at.lte.${nowIso}`)
    .or(`ends_at.is.null,ends_at.gt.${nowIso}`)
    .order("created_at", { ascending: false })
    .limit(1);
  if (error) return null;
  const row = (data ?? [])[0] as unknown as UrgencyCampaign | undefined;
  if (!row) return null;
  return campaignStatus(row) === "live" ? row : null;
}

export function useUrgencyCampaign() {
  const { data } = useQuery({
    queryKey: ["urgency", "campaign"],
    queryFn: fetchLiveCampaign,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  return data ?? null;
}

export function useProductUrgencyLabel(handle: string | undefined) {
  const { data } = useQuery({
    queryKey: ["urgency", "labels"],
    queryFn: async (): Promise<ProductUrgencyLabel[]> => {
      const { data, error } = await supabase
        .from("product_urgency_labels")
        .select("id,product_handle,label,is_enabled");
      if (error) return [];
      return (data ?? []) as unknown as ProductUrgencyLabel[];
    },
    staleTime: 5 * 60_000,
  });
  if (!handle) return null;
  return (data ?? []).find((l) => l.product_handle === handle && l.is_enabled) ?? null;
}

/** Ticks once a second while a countdown is on screen. */
export function useNowTick(active: boolean) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
}
